import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Send, Eye, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useI18n } from '../contexts/I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Marketing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    status: 'draft',
  });

  const { t } = useI18n();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/campaigns`, formData, { withCredentials: true });
      setIsDialogOpen(false);
      setFormData({
        name: '',
        subject: '',
        content: '',
        status: 'draft',
      });
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const handleViewDetails = (campaign) => {
    setSelectedCampaign(campaign);
    setIsDetailsOpen(true);
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    scheduled: 'bg-yellow-100 text-yellow-800',
    sent: 'bg-green-100 text-green-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0176D3]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pages.marketing.title','Marketing Hub')}</h1>
          <p className="text-gray-600 mt-2">{t('pages.marketing.subtitle','Create and manage email campaigns')}</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0176D3] hover:bg-[#0155A8]">
              <Plus className="w-4 h-4 mr-2" />
              {t('pages.marketing.createBtn','Create Campaign')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{t('pages.marketing.dialogTitle','Create New Campaign')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">{t('pages.marketing.nameLabel','Campaign Name *')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="subject">{t('pages.marketing.subjectLabel','Email Subject *')}</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content">{t('pages.marketing.contentLabel','Email Content *')}</Label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="6"
                  required
                />
              </div>
              <div>
                <Label htmlFor="status">{t('pages.marketing.statusLabel','Status')}</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="draft">{t('pages.marketing.draft','Draft')}</option>
                  <option value="scheduled">{t('pages.marketing.scheduled','Scheduled')}</option>
                  <option value="sent">{t('pages.marketing.sent','Sent')}</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('pages.marketing.cancel','Cancel')}
                </Button>
                <Button type="submit" className="bg-[#0176D3] hover:bg-[#0155A8]">
                  {t('pages.marketing.createBtn','Create Campaign')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-500 text-lg">{t('pages.marketing.noItems','No campaigns found. Create your first campaign!')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  <Badge className={statusColors[campaign.status]}>
                    {campaign.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Subject:</p>
                    <p className="text-sm text-gray-900">{campaign.subject}</p>
                  </div>

                  {campaign.status === 'sent' && (
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                          <Send className="w-4 h-4" />
                          <span className="text-xs">{t('pages.marketing.sentLabel','Sent')}</span>
                        </div>
                        <p className="text-xl font-bold text-gray-900">{campaign.sent_count}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
                          <Eye className="w-4 h-4" />
                          <span className="text-xs">{t('pages.marketing.openRate','Open Rate')}</span>
                        </div>
                        <p className="text-xl font-bold text-gray-900">{campaign.open_rate}%</p>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      className="w-full"
                      size="sm"
                      onClick={() => handleViewDetails(campaign)}
                    >
                      {t('pages.marketing.viewDetails','View Details')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{selectedCampaign?.name}</DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium block mb-1">{t('pages.marketing.subjectLabel','Email Subject *')}</span>
                  <span className="text-gray-900">{selectedCampaign.subject}</span>
                </div>
                <div>
                  <span className="font-medium block mb-1">{t('pages.marketing.statusLabel','Status')}</span>
                  <Badge className={statusColors[selectedCampaign.status]}>
                    {selectedCampaign.status}
                  </Badge>
                </div>
                <div>
                  <span className="font-medium block mb-1">{t('pages.marketing.sentLabel','Sent')}</span>
                  <span className="text-gray-900">{selectedCampaign.sent_count}</span>
                </div>
                <div>
                  <span className="font-medium block mb-1">{t('pages.marketing.openRate','Open Rate')}</span>
                  <span className="text-gray-900">{selectedCampaign.open_rate}%</span>
                </div>
              </div>

              <div>
                <span className="font-medium text-sm text-gray-600 block mb-2">{t('pages.marketing.contentLabel','Email Content *')}</span>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-800 whitespace-pre-line">
                  {selectedCampaign.content}
                </div>
              </div>

              {selectedCampaign.scheduled_date && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium block mb-1">{t('pages.marketing.scheduled','Scheduled')}</span>
                  <span className="text-gray-900">
                    {new Date(selectedCampaign.scheduled_date).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="text-xs text-gray-500">
                <p>{t('pages.marketing.createdAt','Created')}: {new Date(selectedCampaign.created_at).toLocaleString()}</p>
                <p>{t('pages.marketing.updatedAt','Last updated')}: {new Date(selectedCampaign.updated_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Marketing;